/**
 * Inline-vs-upload attachment predicates — pure, and deliberately NOT in
 * `attachmentService`.
 *
 * They used to live there, which made them unreachable from anything that is
 * not already willing to load the document picker, blob-util, react-native-fs
 * and react-native-share. Worse for tests: a suite that partially mocks
 * `attachmentService` (and many do) silently loses these functions, so any new
 * caller crashes with "is not a function" in a dozen unrelated suites. Keeping
 * them here means a caller that only needs the predicates takes on nothing.
 *
 * `attachmentService` re-exports all four, so existing imports are unchanged
 * and this module is purely additive.
 */

/** Live write-path values. NULL/undefined means the row predates this column. */
export type AttachmentSource = 'inline' | 'upload';

/**
 * The attachment shape these predicates actually read — structural rather than
 * `SavedAttachment`, so this module needs no import at all.
 */
export interface InlineFilterableAttachment {
  id?: number;
  attachmentSource?: AttachmentSource | null;
  /** List payloads sometimes still use the DB column name. */
  attachment_source?: AttachmentSource | null;
}

/**
 * Canonical reader for `attachment_source`. NULL/undefined/unknown are
 * "written before this marker existed" — never a live `inline`/`upload`
 * decision. Every consumer must go through this helper so that meaning
 * cannot drift.
 */
export function resolveAttachmentSource(
  value: string | null | undefined,
): AttachmentSource | null {
  if (value === 'inline' || value === 'upload') {
    return value;
  }
  return null;
}

export function isInlineAttachment(
  attachment: InlineFilterableAttachment,
): boolean {
  return (
    resolveAttachmentSource(attachment.attachmentSource) === 'inline' ||
    resolveAttachmentSource(attachment.attachment_source) === 'inline'
  );
}

/** Upload-style attachment lists (Supporting Documents, Summary block, subsection paperclip). */
export function attachmentsForUploadStyleDisplay<
  T extends InlineFilterableAttachment,
>(attachments: readonly T[], referencedInlineIds?: ReadonlySet<number>): T[] {
  return attachments.filter(attachment => {
    if (isInlineAttachment(attachment)) {
      return false;
    }
    if (attachment.id != null && referencedInlineIds?.has(attachment.id)) {
      return false;
    }
    return true;
  });
}

export function filterInlineAttachmentsFromSectionMap<
  T extends InlineFilterableAttachment,
>(bySection: Readonly<Record<string, T[]>>): Record<string, T[]> {
  const filtered: Record<string, T[]> = {};
  for (const [sectionId, attachments] of Object.entries(bySection)) {
    const list = attachmentsForUploadStyleDisplay(attachments);
    if (list.length > 0) {
      filtered[sectionId] = list;
    }
  }
  return filtered;
}
